// Metal-glass primitives. These three carry the entire aesthetic; everything
// else is layout + Base UI behavior + Tailwind utilities.
//
//   Surface     — full-bleed page background. Renders the near-black canvas.
//   GlassPanel  — the frosted, hairline-bordered container. Used for chrome
//                 (top bar) and content (detail panels, job rows).
//   Pill        — the pill-shaped button. Composes Base UI buttons cleanly.

import { forwardRef } from "react";
import { cn } from "@/lib/utils";

interface SurfaceProps extends React.HTMLAttributes<HTMLDivElement> {}

export function Surface({ className, children, ...rest }: SurfaceProps) {
	return (
		<div
			className={cn("relative w-full h-full bg-[var(--color-bg)]", className)}
			{...rest}
		>
			{children}
		</div>
	);
}

interface GlassPanelProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * Use `hi` for prominent surfaces (top bar, focused detail panel) — higher
	 * border contrast and slightly more opaque fill so it sits above other
	 * panels visually.
	 */
	variant?: "default" | "hi";
}

export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
	function GlassPanel({ className, variant = "default", children, ...rest }, ref) {
		return (
			<div
				ref={ref}
				className={cn(
					variant === "hi" ? "glass-hi" : "glass",
					"rounded-[var(--radius-panel)]",
					className,
				)}
				{...rest}
			>
				{children}
			</div>
		);
	},
);

interface PillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/**
	 * Icon-only variant: square 32×32 with the icon centered.
	 */
	icon?: boolean;
}

export const Pill = forwardRef<HTMLButtonElement, PillProps>(function Pill(
	{ className, icon, children, type = "button", ...rest },
	ref,
) {
	return (
		<button
			ref={ref}
			type={type}
			className={cn("pill", icon && "pill-icon", className)}
			{...rest}
		>
			{children}
		</button>
	);
});
